/**
 * Single-player Football (Goal Shooting) game-domain types.
 * Ported from packages/amgfootball_v2/lib/core/constants/{game,bet}_constants.dart
 *
 * ──────────────────────────────────────────────────────────────────────────
 * THE BET-ID TRAP (read this before touching anything below)
 * ──────────────────────────────────────────────────────────────────────────
 * The Flutter `BetId` constants have symbol names that are INVERTED relative
 * to the human goal labels, and the history values are cross-wired again on
 * top. We carry the EXACT numeric ids through and key our BetType union by
 * the Flutter symbol names (left=336, center=335, right=337) so every
 * downstream mapping (bet-amount text run, history value, ball-shoot code)
 * matches `game_bloc.dart` 1:1:
 *
 *   BetType 'left'   → betId 336  → text `leftBetAmount`   → history 3 → ballShoot [2,3,6,7]
 *   BetType 'center' → betId 335  → text `centerBetAmount` → history 2 → ballShoot [4,5]
 *   BetType 'right'  → betId 337  → text `rightBetAmount`  → history 1 → ballShoot [8,9]
 *
 * The Rive BettingLayer artboard decides which betId each on-screen goal
 * emits (via `evtBetClick {betID}`); we just echo betId → text/history/shoot
 * exactly as the bloc does. The screen-position vs symbol-name confusion is
 * irrelevant to correctness as long as these tables are preserved verbatim.
 */

export type GamePhase =
  | 'idle'
  | 'initializing'
  | 'modeSelection'
  | 'multiplayerSyncing'
  | 'betting'
  | 'bettingClosed'
  | 'dealing'
  | 'revealing'
  | 'result'
  | 'newRound'
  | 'error';

export type BetType = 'left' | 'center' | 'right';

/** betItemId per BetId constants — DO NOT renumber. */
export const BET_ITEM_ID: Record<BetType, number> = {
  left: 336,
  center: 335,
  right: 337,
};

export function betTypeFromId(id: number): BetType | null {
  switch (id) {
    case 336:
      return 'left';
    case 335:
      return 'center';
    case 337:
      return 'right';
    default:
      return null;
  }
}

/** Main-artboard per-goal bet-amount text-run name. */
export const BET_TEXT_FIELD: Record<BetType, string> = {
  left: 'leftBetAmount',
  center: 'centerBetAmount',
  right: 'rightBetAmount',
};

/** HistoryValue.fromBetId: 336→3, 335→2, 337→1. */
export const HISTORY_VALUE: Record<BetType, 1 | 2 | 3> = {
  left: 3,
  center: 2,
  right: 1,
};

/**
 * intBallShoot trajectory codes by winning bet (game_bloc.dart:506-522).
 * A random element is picked at result time. 0=clear, 1=idle/armed.
 */
export const BALL_SHOOT_BY_BET: Record<BetType, number[]> = {
  left: [2, 3, 6, 7],
  center: [4, 5],
  right: [8, 9],
};

export type GameResult = BetType;

/** History strip value 1/2/3 → which goal (display only). */
export const GAME_RESULT_HISTORY_VALUE: Record<GameResult, 1 | 2 | 3> =
  HISTORY_VALUE;

export interface GameConfig {
  minBet: number;
  maxBet: number;
  chipValues: readonly number[];
  /** [leftOdds, centerOdds, rightOdds] as decimal strings, e.g. "2.00". */
  odds: readonly string[];
  /** [leftCard, rightCard] shown before the first shot. */
  initialCards: readonly number[];
}

export interface GameRound {
  roundId: string;
  nextRoundId: string;
  /** Raw card numbers from the result string (1..52-ish football cards). */
  leftCard: number;
  rightCard: number;
  centerCard: number;
  winningBetId: number;
  result: GameResult;
  totalWinLose: number;
  totalBet: number;
  /** [leftCard, rightCard] to pre-set for the next round (from GameInitialInfo). */
  nextRoundCards: number[];
}

/** Random ball-shoot code for a winning bet (mirrors `_random` pick). */
export function ballShootFor(bet: GameResult): number {
  const opts = BALL_SHOOT_BY_BET[bet];
  return opts[Math.floor(Math.random() * opts.length)] ?? 0;
}
